import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./languageTest.scss";

import IntroSection from "./introSection";
import MicTestSection from "./micTestSection";
import SpeakerTestSection from "./speakerTestSection";
import ReadingSection from "./readingSection";
import ListeningSection from "./listeningSection";
import SpeakingSection from "./speakingSection";

import {
  sendAnswerMessage,
  requestNextSection,
  closeWebsocket,
} from "../../Redux/Action/websocketActions";
import { setLastError, tickOneSecond } from "../../Redux/Reducer/examSlice";
import Webcam from "react-webcam";
import { uploadProctoringImage } from "../../Redux/Action/apiAction";

import store from "../../Redux/store";
import { Alert, Snackbar } from "@mui/material";
import { resolveProctoringEnabled } from "../../utils/proctoring";

const LanguageTest = () => {
  const { token: assignmentId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Resolved from &isProctoringEnabled=true|false on the launch URL (default:
  // proctored). Persisted to sessionStorage so it survives the URL cleanup below.
  const [isProctoringEnabled] = useState(() => resolveProctoringEnabled());

  const [localStep, setLocalStep] = useState(0);
  const [missingQuestions, setMissingQuestions] = useState([]);
  const [exitFsError, setExitFsError] = useState(false);

  const localSteps = ["intro", "mic", "speaker"];
  const exam = useSelector((s) => s.exam);

  const timerRef = useRef(null);
  const webcamRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const authToken = params.get("authToken");
    const candidateTestId = params.get("candidateTestId");
    const invitationToken = params.get("invitationToken");
    const returnUrl = params.get("returnUrl");

    console.log("[proctoring] LanguageTest landing URL", {
      href: window.location.href,
      search: window.location.search,
      isProctoringEnabledParam: params.get("isProctoringEnabled"),
      hasIsProctoringEnabledParam: params.has("isProctoringEnabled"),
      resolvedIsProctoringEnabled: isProctoringEnabled,
      windowWidth: window.innerWidth,
      // .language-container-sm ("Laptop or desktop") is rendered only when proctored
      gateRendered: isProctoringEnabled,
    });

    if (authToken) {
      localStorage.setItem("authToken", authToken);
    }

    if (candidateTestId) {
      localStorage.setItem("candidateTestId", candidateTestId);
    }

    if (invitationToken) {
      localStorage.setItem("LANGUAGE_TEST_INVITE_TOKEN", invitationToken);
    }

    if (returnUrl) {
      localStorage.setItem("LANGUAGE_TEST_RETURN_URL", returnUrl);
    }

    // 🔐 clean URL (security + cleanliness) — but keep isProctoringEnabled in the
    // URL so a refresh / route remount can still resolve it (sessionStorage is
    // only a secondary fallback).
    const cleanedUrl =
      window.location.pathname +
      (isProctoringEnabled ? "" : "?isProctoringEnabled=false");
    window.history.replaceState({}, "", cleanedUrl);
  }, [isProctoringEnabled]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      dispatch(tickOneSecond());
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
      closeWebsocket();
    };
  }, [dispatch]);

  useEffect(() => {
    if (!exam.currentSection) return;
    if (exam.timeRemaining === 0) {
      requestNextSection(store);
    }
  }, [exam.timeRemaining, exam.currentSection]);

  useEffect(() => {
    if (!exam.currentSection) return;
    if (exam.examCompleted) {
      closeWebsocket();
      navigate(`/exam/${assignmentId}/complete`);
    }
  }, [exam.examCompleted, navigate, assignmentId]);

  useEffect(() => {
    if (!isProctoringEnabled) return;
    if (localStep >= localSteps.length) {
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    }
  }, [localStep, isProctoringEnabled]);

  useEffect(() => {
    if (!isProctoringEnabled) return;
    if (localStep < localSteps.length) return;

    const handleFsChange = () => {
      if (!document.fullscreenElement) {
        setExitFsError(true);
        window.needReEnterFullscreen = true;
      }
    };

    document.addEventListener("fullscreenchange", handleFsChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFsChange);
  }, [localStep, isProctoringEnabled]);

  useEffect(() => {
    if (!isProctoringEnabled) return;
    if (localStep < localSteps.length) return;

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") {
        setExitFsError(true);
        window.needReEnterFullscreen = true;
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [localStep, isProctoringEnabled]);

  useEffect(() => {
    if (!isProctoringEnabled) return;
    if (localStep < localSteps.length) return;

    const reenterFsOnClick = () => {
      if (window.needReEnterFullscreen) {
        const el = document.documentElement;
        if (el.requestFullscreen) {
          el.requestFullscreen().catch(() => {});
        }
        window.needReEnterFullscreen = false;
      }
    };

    document.addEventListener("click", reenterFsOnClick);

    return () => document.removeEventListener("click", reenterFsOnClick);
  }, [localStep, isProctoringEnabled]);

  useEffect(() => {
    const section = exam.currentSection;
    if (!section) return;

    const completed = isSectionCompleted(section, exam.answeredQuestions);

    if (completed) {
      requestNextSection(store);
    }
  }, [exam.answeredQuestions, exam.currentSection]);

  useEffect(() => {
    const section = exam.currentSection;
    if (!section) return;

    // flatten all questions
    const allQs = [];
    section.question_groups.forEach((g) => {
      if (g.parent_question) allQs.push(g.parent_question);
      g.child_questions.forEach((c) => allQs.push(c));
    });

    const isCompleted = (q) => {
      if (!q?.previous_answer?.answer) return false;
      const data = q.previous_answer.answer.answer_data;
      if (data?.status === "completed") return true;
      if (typeof data === "string" && data.trim() !== "") return true;
      if (typeof data === "object" && Object.keys(data).length > 0) return true;
      return false;
    };

    const firstIncomplete = allQs.findIndex((q) => !isCompleted(q));

    // If all questions completed → trigger next section ONCE, safely
    if (firstIncomplete === -1) {
      requestNextSection(store);
    }
  }, [exam.currentSection]);

  useEffect(() => {
    if (!isProctoringEnabled) return;
    if (!exam.currentSection) return;

    const CAPTURE_INTERVAL_MS = 10 * 1000;

    const intervalId = setInterval(() => {
      captureDuringLanguageTest();
    }, CAPTURE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [exam.currentSection, isProctoringEnabled]);

  const captureDuringLanguageTest = async () => {
    if (!webcamRef.current) return;

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;

    const blob = await fetch(imageSrc).then((res) => res.blob());
    const file = new File([blob], "user.png", { type: "image/png",});

    const authToken = localStorage.getItem("authToken");
    const invitationToken = localStorage.getItem("LANGUAGE_TEST_INVITE_TOKEN");

    if (!authToken || !invitationToken) return;

    const formData = new FormData();
    formData.append("token", invitationToken);
    formData.append("file", file);

    try {
      await uploadProctoringImage(formData, authToken);
    } catch (err) {
      console.warn("⚠️ Language test proctoring failed");
    }
  };

  const submitAnswer = async ({
    assignment_question_id,
    question_id,
    answer,
  }) => {
    let answerPayload;

    if (
      answer &&
      answer.answer_data &&
      typeof answer.answer_data === "object"
    ) {
      answerPayload = answer.answer_data;
    } else if (answer && answer.answer_data) {
      answerPayload = answer.answer_data;
    } else if (answer && answer.answer_type === "multiple_choice") {
      answerPayload = answer.selected_option;
    } else if (answer && answer.answer_type === "text_input") {
      answerPayload = answer.text_answer;
    } else {
      answerPayload = answer;
    }

    const message = {
      type: "answer",
      assignment_question_id,
      answer: {
        question_id,
        answer_data: answerPayload,
        timestamp: new Date().toISOString(),
      },
    };

    sendAnswerMessage(message, store);
  };

  function computeMissingQuestions(section, answeredQuestions) {
    const missing = [];

    section.question_groups.forEach((g) => {
      if (g.parent_question) {
        const id = g.parent_question.assignment_question_id;
        if (!answeredQuestions[id]) missing.push(id);
      }

      g.child_questions.forEach((c) => {
        if (!answeredQuestions[c.assignment_question_id])
          missing.push(c.assignment_question_id);
      });
    });

    return missing;
  }

  const handleRequestNextSection = () => {
    const section = exam.currentSection;
    const answered = exam.answeredQuestions;

    if (!isSectionCompleted(section, answered)) {
      const missing = computeMissingQuestions(section, answered);
      setMissingQuestions(missing);
      dispatch(setLastError("Please answer all questions before proceeding."));
      return;
    }

    requestNextSection(store);
  };

  const CircularProgress = ({
    size = 140,
    stroke = 6,
    progress = 0,
    children,
  }) => {
    const r = 42;
    const circumference = 2 * Math.PI * r;
    const clamped = Math.max(0, Math.min(1, progress));
    const dashoffset = circumference * (1 - clamped);

    return (
      <div
        className="lt-circ-wrap"
        style={{
          width: size,
          height: size,
          position: "relative",
          top: "90px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg viewBox="0 0 120 120" width={size} height={size}>
          {/* Background circle */}
          <circle
            cx="60"
            cy="60"
            r={r}
            fill="#000"
            stroke="#e6f9ef"
            strokeWidth={stroke}
          />

          {/* Progress */}
          <g transform="rotate(-90 60 60)">
            <circle
              cx="60"
              cy="60"
              r={r}
              fill="transparent"
              stroke="#10B981"
              strokeWidth={stroke}
              strokeDasharray={circumference}
              strokeDashoffset={dashoffset}
              strokeLinecap="round"
            />
          </g>

          {/* Inner black circle */}
          <circle cx="60" cy="60" r={r - stroke - 2} fill="#0b0b0b" />
        </svg>

        {/* Label text */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            fontSize: "16px",
            fontWeight: 600,
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          {children}
        </div>
      </div>
    );
  };

  const TopBar = () => {
    if (!exam.currentSection) return null;

    const section = exam.currentSection;
    const qgroups = section.question_groups || [];

    const flattened = [];
    const groupOffsets = {};

    let runningIndex = 0;

    qgroups.forEach((g) => {
      const startIndex = runningIndex + 1;

      if (g.parent_question) {
        flattened.push({
          ...g.parent_question,
          _isParent: true,
          groupId: g.id,
        });
        runningIndex++;
      }

      (g.child_questions || []).forEach((c) => {
        flattened.push({
          ...c,
          _isParent: false,
          groupId: g.id,
        });
        runningIndex++;
      });

      const endIndex = runningIndex;

      groupOffsets[g.id] = {
        start: startIndex,
        length: endIndex - startIndex + 1,
      };
    });

    const answeredCount = flattened.filter((q) => {
      const backendCompleted =
        q?.previous_answer?.answer?.answer_data?.status === "completed";
      const savedNow =
        exam.answeredQuestions[q.assignment_question_id]?.status === "saved";

      return backendCompleted || savedNow;
    }).length;

    let currentQIndex = answeredCount + 1;
    const totalQuestions = flattened.length;

    if (currentQIndex > totalQuestions) currentQIndex = totalQuestions;
    const questionProgress = totalQuestions > 0 ? currentQIndex / totalQuestions : 0;

    let timerTotalSeconds = section.time_limit_seconds ?? null;

    if (!timerTotalSeconds) {timerTotalSeconds = exam.totalExamTimeMinutes ? exam.totalExamTimeMinutes * 60 : exam.timeRemaining;}

    const timerProgress = exam.timeRemaining && timerTotalSeconds ? exam.timeRemaining / timerTotalSeconds : 0;

    return (
      <div
        className="lt-topbar"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 0",
          marginBottom: "0",
          width: "100%",
          maxWidth: "1128px",
          height: "60px",
        }}
      >
        <CircularProgress progress={questionProgress}>
          <div>
            {currentQIndex}/{totalQuestions}
          </div>
          <div>Question</div>
        </CircularProgress>

        <div style={{ textAlign: "center", flex: 1 }}>
          <h4>{section.section_name}</h4>
        </div>

        <CircularProgress progress={timerProgress}>
          <div>{formatClock(exam.timeRemaining)}</div>
          <div>Remaining</div>
        </CircularProgress>
      </div>
    );
  };

  const renderServerSection = () => {
    const section = exam.currentSection;

    if (!section) {
      return (
        <div className="language-right-section">
          <div className="loader">Waiting for section data…</div>
        </div>
      );
    }

    const qgroups = section.question_groups || [];
    const flattened = [];
    const groupOffsets = {};

    let runningIndex = 0;
    qgroups.forEach((g) => {
      const startIndex = runningIndex + 1;

      if (g.parent_question) {
        flattened.push({
          ...g.parent_question,
          _isParent: true,
          groupId: g.id,
          groupType: g.type,
        });
        runningIndex++;
      }

      (g.child_questions || []).forEach((c) => {
        flattened.push({
          ...c,
          _isParent: false,
          groupId: g.id,
          groupType: g.type,
        });
        runningIndex++;
      });

      const endIndex = runningIndex;
      groupOffsets[g.id] = {
        start: startIndex,
        length: endIndex - startIndex + 1,
      };
    });

    const isCompleted = (q) => {
      if (!q?.previous_answer?.answer) return false;
      const data = q.previous_answer.answer.answer_data;
      if (data?.status === "completed") return true;
      if (typeof data === "string" && data.trim() !== "") return true;
      if (typeof data === "object" && Object.keys(data).length > 0) return true;
      return false;
    };

    flattened.forEach((q, i) => (q._flatIndex = i + 1));

    const firstIncomplete = flattened.findIndex((q) => !isCompleted(q));

    const containsReadingFluency = flattened.some((q) => q.content?.question_type === "reading_fluency_individual");

    if (containsReadingFluency) {
      return (
        <ReadingSection
          section={section}
          flattenedQuestions={flattened}
          submitAnswer={submitAnswer}
          onRequestNextSection={handleRequestNextSection}
          missingQuestions={missingQuestions}
          assignmentId={assignmentId}
        />
      );
    }

    // let currentFlatIndex = 1;
    let currentFlatIndex = firstIncomplete + 1;

    try {
      const gIndex = section.current_group_index;
      const qIndex = section.current_question_index;

      if (typeof gIndex === "number" && typeof qIndex === "number") {
        const group = qgroups[gIndex - 1];
        if (group && groupOffsets[group.id]) {
          currentFlatIndex = groupOffsets[group.id].start + (qIndex - 1);
        } else {
          if (
            typeof qIndex === "number" &&
            qIndex >= 1 &&
            qIndex <= flattened.length
          ) {
            currentFlatIndex = qIndex;
          } else {
            currentFlatIndex = 1;
          }
        }
      } else if (typeof section.current_question_index === "number") {
        const idx = section.current_question_index;
        if (idx >= 1 && idx <= flattened.length) currentFlatIndex = idx;
        else currentFlatIndex = 1;
      } else {
        currentFlatIndex = 1;
      }
    } catch (err) {
      currentFlatIndex = 1;
    }
    const current = flattened[currentFlatIndex - 1] || flattened[0];
    const sectionName = (section.section_name || "").toLowerCase();

    // Listening section
    if (sectionName.includes("listening")) {
      return (
        <ListeningSection
          section={section}
          flattenedQuestions={flattened}
          submitAnswer={submitAnswer}
          onRequestNextSection={handleRequestNextSection}
        />
      );
    }

    // Speaking section (explicit speaking sections)
    if (sectionName.includes("speaking")) {
      return (
        <SpeakingSection
          section={section}
          flattenedQuestions={flattened}
          submitAnswer={submitAnswer}
          onRequestNextSection={handleRequestNextSection}
          assignmentId={assignmentId}
        />
      );
    }

    // DEFAULT → READING
    return (
      <ReadingSection
        section={section}
        flattenedQuestions={flattened}
        submitAnswer={submitAnswer}
        onRequestNextSection={handleRequestNextSection}
        missingQuestions={missingQuestions}
        assignmentId={assignmentId}
      />
    );
  };

  if (localStep < localSteps.length) {
    const step = localSteps[localStep];

    return (
        <>
          <div
            className={`language-container${
              isProctoringEnabled ? "" : " lt-responsive"
            }`}
          >
            <div className="language-test-section d-flex">
              {step === "intro" && (
                <IntroSection onNext={() => setLocalStep((s) => s + 1)} />
              )}
              {step === "mic" && (
                <MicTestSection onNext={() => setLocalStep((s) => s + 1)} />
              )}
              {step === "speaker" && (
                <SpeakerTestSection onNext={() => setLocalStep((s) => s + 1)} />
              )}
            </div>
          </div>
          {isProctoringEnabled && (
            <div className="language-container-sm">
              <p>Language test needs to be taken on Laptop or desktop.</p>
            </div>
          )}
        </>
    );
  }

  function isSectionCompleted(section, answeredQuestions) {
    if (!section) return false;

    const allQs = [];

    section.question_groups.forEach((group) => {
      if (group.parent_question)
        allQs.push(group.parent_question);

      group.child_questions.forEach((c) => allQs.push(c));
    });

    return allQs.every((q) => {
      const backendCompleted =
        q?.previous_answer?.answer?.answer_data?.status === "completed";

      const savedNow =
        answeredQuestions[q.assignment_question_id]?.status === "saved";

      return backendCompleted || savedNow;
    });
  }

  return (
    <>
      <div
        className={`language-container${
          isProctoringEnabled ? "" : " lt-responsive"
        }`}
      >
        {exam.currentSection && <TopBar />}
        <div className="language-test-section d-flex">
          {exam.loadingSection ? (
            <div className="language-right-section">
              <div className="loader">Loading section…</div>
            </div>
          ) : (
            renderServerSection()
          )}
        </div>

        <div className="language-bottom-watermark">
          <img src="/images/gappeo-logo.svg" alt="" width={20} height={17} />
          <h3>GAPPEO AUDIO TEST</h3>
        </div>
        <Snackbar
          open={exitFsError}
          autoHideDuration={2500}
          onClose={() => setExitFsError(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            Leaving fullscreen or switching tab is not allowed
          </Alert>
        </Snackbar>
      </div>
      {isProctoringEnabled && (
        <div style={{ position: "absolute", bottom: 0, right: 0, visibility: "hidden" }}>
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/png"
            mirrored
            screenshotQuality={1}
          />
        </div>
      )}
      {isProctoringEnabled && (
        <div className="language-container-sm">
          <p>Language test needs to be taken on Laptop or desktop.</p>
        </div>
      )}
    </>
  );
};

function formatClock(seconds) {
  if (seconds == null) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default LanguageTest;
